import { Link } from 'react-router-dom'
import { Add, Backup, Delete, Edit } from '@mui/icons-material'
import {
  Skeleton,
  Stack,
  Link as MUILink,
  Button,
  Box,
  Typography,
} from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import useOverlay from '@/hooks/useOverlay'
import {
  useGetOrganization,
  useGetOrganizations,
} from '@/service-library/hooks/organizations'
import { Organization } from '@/types/organizations'
import LabeledData from '@/components/labeled-data/LabeledData'
import LargeHeading from '@/components/large-heading/LargeHeading'
import CopyIDButton from '../copy-id-button/CopyIDButton'
import AddEditOrganizationDialog from './AddEditOrganizationDialog'
import ChildrenOrganizations from './ChildrenOrganizations'
import DeleteOrganizationDialog from './DeleteOrganizationDialog'

type OrganizationDetailsProps = {
  organization: Organization
}

export default function OrganizationDetails({
  organization,
}: OrganizationDetailsProps) {
  const { isAuthorized: canEditOrg } = useStytchIsAuthorized('pd.org', 'edit')

  const addSubOrgOverlay = useOverlay()
  const editOverlay = useOverlay()
  const deleteOverlay = useOverlay()

  const { organizations = [], isPending } = useGetOrganizations({
    filters: {
      parent_org_id: organization.id,
      ordering: '-parent_org_id,name,id',
      limit: '1000',
    },
    enabled: !!organization.id,
  })

  const { organization: parentOrganization, isPending: isLoadingParent } =
    useGetOrganization({
      id: organization.parent_org_id as string,
      filters: {
        fields__include: 'org_type',
      },
      enabled: !!organization.parent_org_id,
    })

  return (
    <Stack spacing={2} sx={{ p: 2, pt: 3, flexGrow: 1, overflow: 'auto' }}>
      <div>
        <LargeHeading
          heading={organization.name}
          subHeading={
            organization.parent_org_id ? 'Organization' : 'Root Organization'
          }
          actions={
            <>
              {canEditOrg ? (
                <>
                  <Button
                    variant="text"
                    startIcon={<Edit />}
                    onClick={editOverlay.open}
                  >
                    Edit
                  </Button>
                  {organization.parent_org_id && (
                    <Button
                      variant="text"
                      color="error"
                      startIcon={<Delete />}
                      onClick={deleteOverlay.open}
                    >
                      Delete
                    </Button>
                  )}

                  <AddEditOrganizationDialog
                    overlay={editOverlay}
                    organization={organization}
                  />
                  <DeleteOrganizationDialog
                    overlay={deleteOverlay}
                    organization={organization}
                  />
                </>
              ) : undefined}
              <CopyIDButton stringToCopy={organization.id} />
            </>
          }
        />
      </div>

      {organization.is_relay_data_provider && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Backup fontSize="small" />
          <Typography variant="body2" color="textSecondary">
            The details of this organization will be used to pull and push data
            from an external system.
          </Typography>
        </Stack>
      )}

      {organization.parent_org_id && (
        <LabeledData
          label="Organization Type"
          data={organization.org_type?.name || 'None'}
          dataColor={organization.org_type ? undefined : 'text.secondary'}
        />
      )}
      {organization.code && (
        <LabeledData label="Identifier" data={organization.code} />
      )}
      {organization.parent_org_id && (
        <LabeledData
          label="Parent Organization"
          data={
            <>
              {isLoadingParent ? (
                <Skeleton width={125} />
              ) : (
                <MUILink
                  component={Link}
                  underline="hover"
                  to={parentOrganization?.id || ''}
                >
                  {parentOrganization?.name}
                </MUILink>
              )}
            </>
          }
        />
      )}

      {/* Need Box to allow scrolling */}
      <Box>
        <ChildrenOrganizations
          organizations={organizations}
          isLoading={isPending}
          noRecordsToDisplay="No sub-organizations have been added to this organization."
          title="Sub-Organizations"
          renderTableActions={
            canEditOrg
              ? () => {
                  return (
                    <Button
                      onClick={addSubOrgOverlay.open}
                      startIcon={<Add />}
                      variant="text"
                    >
                      New Organization
                    </Button>
                  )
                }
              : undefined
          }
        />
      </Box>

      <AddEditOrganizationDialog
        overlay={addSubOrgOverlay}
        parentOrgId={organization.id}
      />
    </Stack>
  )
}
